// ── AREA FIT — does the county trigger pay when THIS farm has a bad year? ─────
//
// The wheat recommendation turns on one economic question:
//
//     when this farm has a bad wheat year, does the area trigger also have one?
//
// Everything else — the 75/80/85 ladder, whether SCO and ECO are worth their
// premium — is downstream of that. Until 2026-09-01 the engine answered it with
// a Pearson correlation of the farm's yields against NASS **STATE** wheat yield,
// and that is the wrong index by construction: SCO and ECO do not settle on the
// state. They settle on the RMA **COUNTY** yield and revenue, which is what
// `sco_eco_county` holds.
//
// Measured on the live book 2026-09-01 (198 wheat units carrying a usable yield
// series, tools/wheat-corr-measure.js):
//
//   correlation resolvable   state 181 units · county 167 units
//   median r                 state 0.42   ·  county 0.49
//   |r_state - r_county|     median 0.19, max 0.98, ABOVE 0.30 on 53 of 167
//   recommendation changed   55 of 198 units, 6,056 acres
//
// So this is not a refinement. On 28% of the book the shipped engine recommends
// a coverage level chosen by how the farm tracks a state average that pays
// nobody.
//
// ⚠ TWO THINGS WERE TESTED AND REFUTED, and they are recorded here so the next
// reader does not re-derive them:
//
//   1. TREND INFLATION IS NOT A PROBLEM HERE, so nothing is detrended. The
//      hypothesis was that farm and area yields share a technology trend, so a
//      raw Pearson would be inflated and would push farms into "the area carries
//      you, drop to 75%" on trend alone. Measured: median (r_raw - r_detrended)
//      is **-0.01 against the state and 0.00 against the county**, and the sign
//      is positive on only 82/181 and 84/167 — a coin flip. Over the 7-9 year
//      windows this book actually has, detrending moves 30 units' recommendation
//      while removing no measurable bias, which is noise, not a correction.
//
//   2. `sco_eco_county.revenue_ratio` IS A PERCENT, NOT A FRACTION. It runs
//      37.4 to 139.1, and a `< 0.86` test — the obvious one, and the one written
//      first here — matches **zero rows in every year of the table**. That reads
//      as "SCO has never triggered", which is false: SCO paid in 129 of 183
//      county-rows in 2019, 67 in 2025, 48 in 2024, 25 in 2018. Same shape as
//      the mm_net_pct_oi fraction-vs-percent bug already recorded in CLAUDE.md.
//      `SCO_TRIGGER_PCT` below is 86, not 0.86, deliberately.
//
// ── WHY DOWNSIDE CO-MOVEMENT AND NOT JUST PEARSON ────────────────────────────
// Pearson weights a good year exactly as much as a bad one, and the grower is
// not buying protection against good years. Two farms can share an r of 0.55 and
// have opposite answers: one whose losses always land in a county-payout year,
// and one whose losses are its own. So the fit carries BOTH — r for the general
// relationship, and a measured hit rate for the question actually being asked.
//
// The hit rate is the honest statistic AND the thin one. County history begins
// 2017 (9 settled years, 2017-2025) and a typical farm has 1-3 loss years in it,
// so a hit rate off 2 observations is not evidence. `MIN_LOSS_YEARS` is the
// floor and a fit below it reports INSUFFICIENT_HISTORY rather than a number.
// That is the same reasoning as pctileOf's 40-observation floor in the briefs.

// ── constants, each sourced or measured ──────────────────────────────────────

// ⚠ SCO'S TOP MOVED FROM 86% TO 90%, EFFECTIVE CROP YEAR 2027 — so the stored
// `sco_payout_pct` column measures a product that no longer exists, and reading
// it would rate the WRONG endorsement for a 2027 renewal.
//
// Sourced, not inferred. The One Big Beautiful Bill Act amended the SCO
// endorsement to be purchasable regardless of the insured's FSA ARC election
// AND raised the maximum coverage level from 86 to 90 percent (RMA MGR-25-006);
// the endorsement change lands "effective for the 2027 and succeeding crop
// years" (RMA PM-26-036). It is corroborated inside this repo by the 2027 ADM
// itself: `quoteAreaRP2027` computes the SCO band as `0.90 - baseCl`, and ECO's
// band as a flat 0.05 — i.e. SCO level→90, ECO 90→95.
//
// It is worth 28% MORE TRIGGER FREQUENCY, measured over the 1,652 settled
// WHEAT county-years in NY/PA/OH/MD: 291 land below 86, and **82 more land
// between 86 and 90** — 373 below the 2027 trigger. Rating a 2027 renewal off
// the 86% payout column understates how often the endorsement pays by that much.
//
// So the forward-looking trigger is computed from `revenue_ratio` against
// SCO_TRIGGER_PCT, and the stored payout columns are kept only for the
// historical 86% band (`areaTriggeredAsFiled`) where the question is genuinely
// "what did the AIP actually pay".
export const SCO_TRIGGER_PCT = 90;        // CY2027+ (OBBBA); was 86 through CY2026
export const SCO_TRIGGER_PCT_LEGACY = 86; // what `sco_payout_pct` records
// With SCO reaching 90, ECO is the 90→95 band. ECO-90 is subsumed and is not
// a separate 2027 election.
export const ECO_TOP_PCT = 95;

// A farm "loss year" for fit purposes is a yield below this fraction of its own
// approved yield. 80% is the MPCI anchor level — the band the whole 75/80/85
// decision is about — so the loss definition and the decision share a threshold.
export const FARM_LOSS_LEVEL = 0.80;

// Floors. Below either, the fit is INSUFFICIENT_HISTORY and says which floor it
// missed. Never silently treated as average.
export const MIN_OVERLAP_YEARS = 5;   // overlapping farm+county years for r
export const MIN_LOSS_YEARS = 3;      // farm loss years before a hit rate means anything

// Fit-state thresholds. r is the general relationship; hit is the downside one.
// Measured book medians 2026-09-01: r_county median 0.49.
export const R_STRONG = 0.60;
export const R_WEAK = 0.30;
export const HIT_STRONG = 0.60;
export const HIT_WEAK = 0.34;

export const FIT = {
  STRONG: "STRONG_AREA_FIT",
  GOOD: "GOOD_VALUE_SOME_BASIS_RISK",
  INSUFFICIENT: "INSUFFICIENT_HISTORY",
  WEAK: "WEAK_AREA_FIT",
};

export const FIT_LABEL = {
  [FIT.STRONG]: "Strong",
  [FIT.GOOD]: "Good, some basis risk",
  [FIT.INSUFFICIENT]: "Not enough history",
  [FIT.WEAK]: "Weak",
};

// ── statistics ───────────────────────────────────────────────────────────────

// Pearson over paired samples. Returns null rather than a number it cannot
// stand behind — fewer than 4 pairs, or a degenerate (zero-variance) series.
export function pearsonR(a, b) {
  const n = Math.min(a.length, b.length);
  if (n < 4) return null;
  let ma = 0, mb = 0;
  for (let i = 0; i < n; i++) { ma += a[i]; mb += b[i]; }
  ma /= n; mb /= n;
  let sab = 0, saa = 0, sbb = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - ma, db = b[i] - mb;
    sab += da * db; saa += da * da; sbb += db * db;
  }
  return (saa > 0 && sbb > 0) ? sab / Math.sqrt(saa * sbb) : null;
}

// The county reference, keyed by year, out of `sco_eco_county` rows.
// A row with no settled final yield is NOT a year — 2026 is expected-only, and
// treating an unsettled year as an observation would let this season vote on its
// own recommendation.
export function areaIndexByYear(rows, state, county) {
  const st = String(state || "").toUpperCase();
  const co = String(county || "").toUpperCase();
  const out = {};
  for (const r of (rows || [])) {
    if (String(r.state || "").toUpperCase() !== st) continue;
    if (String(r.county || "").toUpperCase() !== co) continue;
    const yr = r.crop_year != null ? +r.crop_year : null;
    if (yr == null) continue;
    const fin = r.rma_final_yield != null ? +r.rma_final_yield : null;
    if (fin == null || !(fin > 0)) continue;
    out[yr] = {
      year: yr,
      finalYield: fin,
      expectedYield: r.expected_yield != null ? +r.expected_yield : null,
      // percent, not a fraction — see the header note
      revenueRatioPct: r.revenue_ratio != null ? +r.revenue_ratio : null,
      scoPaidPct: r.sco_payout_pct != null ? +r.sco_payout_pct : null,
      eco90PaidPct: r.eco90_payout_pct != null ? +r.eco90_payout_pct : null,
      eco95PaidPct: r.eco95_payout_pct != null ? +r.eco95_payout_pct : null,
    };
  }
  return out;
}

// WOULD A 2027-STRUCTURE SCO HAVE TRIGGERED in this county-year?
//
// Computed from `revenue_ratio` against the CURRENT trigger, never read off the
// stored payout column — that column is the 86% product (see the constants
// note). The ratio is RMA's own settled number and the rule is arithmetic, so
// replaying it forward is a restatement, not a model.
export function areaTriggered(idx, triggerPct = SCO_TRIGGER_PCT) {
  if (!idx || idx.revenueRatioPct == null) return null;
  return idx.revenueRatioPct < triggerPct;
}

// What the AIP ACTUALLY paid, at the 86% band that applied in those years. Kept
// separate on purpose: "what would today's product have done" and "what did the
// old product do" are different questions, and collapsing them is how a renewal
// gets rated on a retired endorsement.
export function areaTriggeredAsFiled(idx) {
  if (!idx) return null;
  if (idx.scoPaidPct != null) return idx.scoPaidPct > 0;
  if (idx.revenueRatioPct == null) return null;
  return idx.revenueRatioPct < SCO_TRIGGER_PCT_LEGACY;
}

// ── the fit ──────────────────────────────────────────────────────────────────
//
// farmSeries : [{ yr, y }] — the unit's own acre-weighted yield history
// areaRows   : raw sco_eco_county rows (any state/county; filtered here)
// aph        : the unit's approved yield, for the farm-loss threshold
//
// Returns a fully self-describing object. Every consumer reads `usable` before
// reading `r` or `hitRate`: a fit that could not be measured must never be
// mistaken for a fit that measured badly.
export function areaFit({ farmSeries, areaRows, state, county, aph, lossLevel = FARM_LOSS_LEVEL, triggerPct = SCO_TRIGGER_PCT }) {
  const idxByYear = areaIndexByYear(areaRows, state, county);
  const years = Object.keys(idxByYear).map(Number).sort((a, b) => a - b);
  const series = (farmSeries || []).filter((o) => o && o.y != null && +o.y > 0);

  const base = {
    source: "sco_eco_county",
    areaBasis: "RMA county final yield + settled revenue ratio",
    triggerPct,
    triggerNote: triggerPct === SCO_TRIGGER_PCT
      ? "SCO tops out at " + SCO_TRIGGER_PCT + "% of county revenue for CY2027 (OBBBA; was " + SCO_TRIGGER_PCT_LEGACY + "%)"
      : "area trigger scored at " + triggerPct + "% of county revenue",
    state: String(state || "").toUpperCase(),
    county: String(county || "").toUpperCase(),
    areaYearsAvailable: years.length,
    areaYearRange: years.length ? [years[0], years[years.length - 1]] : null,
  };

  // -- THE COUNTY'S OWN EXPECTED YIELD, AND THIS FARM AGAINST IT ------------
  //
  // Kevin, 2026-09-10: "we should also show the Farmers APH CV and County
  // Basis on the quote to help explain why we are recommending what we are."
  // The Coverage Advisor's per-county book has printed both for months; the
  // quote itself printed the fit LABEL and neither number, so a reader could
  // see the conclusion and not the two figures behind it.
  //
  // It is derived HERE rather than on the card because the card is not the
  // only reader - the printed sheet renders the same recommendation, and this
  // repo's record on a rule defined twice is that it drifts (three news
  // handlers, two guarantee engines, three copies of normFarmerName).
  //
  // WARNING: THE MOST RECENT YEAR, NOT A MEAN. RMA restates the county
  // expected yield each crop year and it trends with technology, so averaging
  // nine years of it produces a denominator that belongs to no year and reads
  // low against a current APH. This is what the grower is quoted against THIS
  // year, and the year it came from is carried beside it.
  //
  // WARNING: AND IT IS DESCRIPTIVE, NOT A DECISION INPUT. Nothing below reads
  // countyExpected or basis; the fit is decided by the correlation and the
  // measured downside hit rate exactly as before. A basis near 1.0 says the
  // farm yields about what its county does, which is context for how an AREA
  // product is likely to behave - it is not evidence, and a fit rule built on
  // a farm/county ratio has already been measured and refused once.
  // WARNING: READ IT OFF THE RAW ROWS, NOT idxByYear. That index requires a
  // SETTLED rma_final_yield, which is exactly what the current crop year does
  // not have - and the current year is the one a quote is written against. On
  // NY STEUBEN wheat, measured 2026-09-10: CY2026 carries expected 76.3 with a
  // null final, so an index-based read fell back to CY2025 (74.1) and the quote
  // was quoting a farm against a county expectation a year out of date.
  let countyExpected = null, latestExpYear = null;
  for (const r of (areaRows || [])) {
    if (String(r.state || "").toUpperCase() !== base.state) continue;
    if (String(r.county || "").toUpperCase() !== base.county) continue;
    const yr = r.crop_year != null ? +r.crop_year : null;
    const ey = r.expected_yield != null ? +r.expected_yield : null;
    if (yr == null || !(ey > 0)) continue;
    if (latestExpYear == null || yr > latestExpYear) { latestExpYear = yr; countyExpected = ey; }
  }
  base.countyExpected = countyExpected;
  base.countyExpectedYear = latestExpYear;
  // NULL, never 1.0: "we could not measure it" and "the farm matches its
  // county" are different statements and only one of them is reassuring.
  base.basis = (countyExpected > 0 && +aph > 0) ? +aph / countyExpected : null;

  const pairs = series.filter((o) => idxByYear[o.yr]);
  const nOverlap = pairs.length;

  // General relationship
  const r = nOverlap >= MIN_OVERLAP_YEARS
    ? pearsonR(pairs.map((o) => o.y), pairs.map((o) => idxByYear[o.yr].finalYield))
    : null;

  // Downside co-movement — the question actually being asked.
  const A = +aph;
  let farmLossYears = [], covered = [], missed = [], areaOnly = [];
  let coveredLegacy = 0, nLossLegacy = 0;
  if (A > 0) {
    for (const o of pairs) {
      const idx = idxByYear[o.yr];
      const trig = areaTriggered(idx, triggerPct);
      if (trig == null) continue;
      const farmLoss = o.y < lossLevel * A;
      if (farmLoss) {
        farmLossYears.push(o.yr);
        (trig ? covered : missed).push(o.yr);
        // the same year scored against the retired 86% product, so the page can
        // show what the trigger change is worth to THIS farm rather than a
        // book-wide average
        nLossLegacy++;
        if (areaTriggeredAsFiled(idx)) coveredLegacy++;
      } else if (trig) {
        areaOnly.push(o.yr);
      }
    }
  }
  const nLoss = farmLossYears.length;
  const hitRate = nLoss >= MIN_LOSS_YEARS ? covered.length / nLoss : null;
  const hitRateLegacy = nLossLegacy >= MIN_LOSS_YEARS ? coveredLegacy / nLossLegacy : null;

  // Why a fit is unusable is part of the answer, not an omission.
  const reasons = [];
  if (!years.length) reasons.push("no RMA county history for " + base.county + ", " + base.state);
  if (nOverlap < MIN_OVERLAP_YEARS) reasons.push(nOverlap + " overlapping year" + (nOverlap === 1 ? "" : "s") + " (need " + MIN_OVERLAP_YEARS + ")");
  if (r == null && nOverlap >= MIN_OVERLAP_YEARS) reasons.push("yield history has no variation to correlate");
  if (hitRate == null) reasons.push(nLoss + " farm loss year" + (nLoss === 1 ? "" : "s") + " on record (need " + MIN_LOSS_YEARS + " to rate the downside)");

  const usable = r != null;

  let fit;
  if (!usable) {
    fit = FIT.INSUFFICIENT;
  } else if (hitRate != null) {
    // Both measures present: the downside decides, r breaks ties.
    if (hitRate >= HIT_STRONG && r >= R_WEAK) fit = FIT.STRONG;
    else if (hitRate <= HIT_WEAK || r < R_WEAK) fit = FIT.WEAK;
    else fit = FIT.GOOD;
  } else {
    // r only. It cannot earn STRONG on its own — a general relationship is not a
    // measured downside — so the best an r-only fit reaches is GOOD.
    if (r >= R_STRONG) fit = FIT.GOOD;
    else if (r < R_WEAK) fit = FIT.WEAK;
    else fit = FIT.GOOD;
  }

  return {
    ...base,
    fit,
    fitLabel: FIT_LABEL[fit],
    usable,
    r,
    nOverlap,
    overlapYears: pairs.map((o) => o.yr),
    hitRate,
    hitRateLegacy,
    nLossYears: nLoss,
    lossYears: farmLossYears,
    coveredYears: covered,
    missedYears: missed,
    areaOnlyYears: areaOnly,
    lossLevel,
    reasons,
  };
}

// One sentence a person can check, for each fit state. Deterministic; no prose
// is generated anywhere else.
export function areaFitSentence(f, stLabel) {
  if (!f) return "Area fit could not be measured.";
  const co = f.county ? f.county.charAt(0) + f.county.slice(1).toLowerCase() : "the county";
  const rTxt = f.r != null ? "corr " + f.r.toFixed(2) + " over " + f.nOverlap + " yrs" : null;
  const hTxt = f.hitRate != null
    ? co + " triggered in " + f.coveredYears.length + " of your " + f.nLossYears + " loss years"
    : null;
  switch (f.fit) {
    case FIT.STRONG:
      return "Your losses and " + co + " County's line up — " + [hTxt, rTxt].filter(Boolean).join(", ") + ". The area trigger has been paying when you needed it.";
    case FIT.WEAK:
      return "Your yields move largely independently of " + co + " County — " + [hTxt, rTxt].filter(Boolean).join(", ") + ". SCO/ECO are still cheap, but they may miss a loss that is yours alone.";
    case FIT.INSUFFICIENT:
      return "Not enough history to judge how well " + co + " County's trigger tracks you: " + f.reasons.join("; ") + ".";
    default:
      return "Reasonable fit to " + co + " County — " + [hTxt, rTxt].filter(Boolean).join(", ") + ". Good value, with some basis risk left on the table.";
  }
}
